import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { LEAD_STATUS } from "../config/constants";

const sanitizeNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(/\s/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const toDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const leadService = {
  // Créer un nouveau lead
  async createLead(leadData, userId) {
    try {
      const expectedRevenue = sanitizeNumber(leadData.expectedRevenue);
      const closedRevenue = sanitizeNumber(leadData.closedRevenue);
      const docRef = await addDoc(collection(db, "leads"), {
        ...leadData,
        assignedToUserId: userId,
        createdBy: userId,
        status: "nouveau",
        expectedRevenue,
        closedRevenue,
        revenueCurrency: leadData.revenueCurrency || "XOF",
        expectedCloseDate: toDateValue(leadData.expectedCloseDate),
        convertedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error("Error creating lead:", error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les leads d'un commercial
  async getLeadsByUser(userId) {
    try {
      const q = query(
        collection(db, "leads"),
        where("assignedToUserId", "==", userId),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error fetching leads:", error);
      return [];
    }
  },

  // Récupérer tous les leads (Admin)
  async getAllLeads() {
    try {
      const q = query(collection(db, "leads"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error fetching all leads:", error);
      return [];
    }
  },

  // Mettre à jour un lead
  async updateLead(leadId, updateData) {
    try {
      const leadRef = doc(db, "leads", leadId);
      const payload = { ...updateData };

      if (Object.prototype.hasOwnProperty.call(updateData, "expectedRevenue")) {
        payload.expectedRevenue = sanitizeNumber(updateData.expectedRevenue);
      }

      if (Object.prototype.hasOwnProperty.call(updateData, "closedRevenue")) {
        payload.closedRevenue = sanitizeNumber(updateData.closedRevenue);
      }

      if (Object.prototype.hasOwnProperty.call(updateData, "revenueCurrency")) {
        payload.revenueCurrency = updateData.revenueCurrency || "XOF";
      }

      if (
        Object.prototype.hasOwnProperty.call(updateData, "expectedCloseDate")
      ) {
        payload.expectedCloseDate = toDateValue(updateData.expectedCloseDate);
      }

      await updateDoc(leadRef, {
        ...payload,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error("Error updating lead:", error);
      return { success: false, error: error.message };
    }
  },

  // Changer le statut d'un lead
  async changeStatus(leadId, newStatus, userId, options = {}) {
    try {
      const updates = { status: newStatus };

      if (Object.prototype.hasOwnProperty.call(options, "expectedRevenue")) {
        updates.expectedRevenue = sanitizeNumber(options.expectedRevenue);
      }

      if (Object.prototype.hasOwnProperty.call(options, "revenueCurrency")) {
        updates.revenueCurrency = options.revenueCurrency || "XOF";
      }

      if (newStatus === LEAD_STATUS.CONVERTED) {
        updates.convertedAt = serverTimestamp();
        if (Object.prototype.hasOwnProperty.call(options, "closedRevenue")) {
          updates.closedRevenue = sanitizeNumber(options.closedRevenue);
        }
      } else if (newStatus !== LEAD_STATUS.CONVERTED) {
        updates.convertedAt = null;
        if (Object.prototype.hasOwnProperty.call(options, "closedRevenue")) {
          updates.closedRevenue = sanitizeNumber(options.closedRevenue);
        }
      }

      await this.updateLead(leadId, updates);

      // Enregistrer dans l'historique
      await addDoc(collection(db, "leadStatusHistory"), {
        leadId,
        newStatus,
        changedBy: userId,
        changedAt: serverTimestamp(),
        closedRevenue: Object.prototype.hasOwnProperty.call(
          updates,
          "closedRevenue"
        )
          ? updates.closedRevenue
          : null,
      });

      return { success: true };
    } catch (error) {
      console.error("Error changing status:", error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer un lead par ID
  async getLeadById(leadId) {
    try {
      const docSnap = await getDoc(doc(db, "leads", leadId));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching lead:", error);
      return null;
    }
  },
};
